import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from '@mui/material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import ModulePageHeader from '../../common/ModulePageHeader';
import { FiscalYearPicker } from '../LedgerPickers';
import { getErrorMessage } from './BudgetingShared';

// Rollover copies one fiscal year's budget lines into another year as DRAFT
// (verified against BudgetService.rollover). Single action - the backend
// returns {from_fiscal_year_id, to_fiscal_year_id, copied,
// skipped_duplicates} and exposes no listing, so the result panel below is
// the confirmation. Duplicates (same account+period already in the target
// year) are skipped, never overwritten.
const RolloverTab = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.BUDGETING, ACTIONS.CREATE);
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [fromYearId, setFromYearId] = useState('');
  const [toYearId, setToYearId] = useState('');
  const [approvedOnly, setApprovedOnly] = useState(true);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  if (!can(RESOURCES.LEDGER.BUDGETING, ACTIONS.READ)) {
    return <Alert severity="error">You do not have permission to view Budget Rollover.</Alert>;
  }

  const runRollover = () => {
    if (!fromYearId || !toYearId) {
      showSnackbar('Source and target fiscal years are required', 'error');
      return;
    }
    if (String(fromYearId) === String(toYearId)) {
      showSnackbar('Source and target years must differ', 'error');
      return;
    }
    confirm({
      title: 'Run rollover',
      message: `Copy ${approvedOnly ? 'APPROVED' : 'all'} budget lines into the target year as DRAFT? Existing account+period lines there are skipped.`,
      onConfirm: async () => {
        try {
          setSaving(true);
          const res = await ledgerAPI.createBudgetRollover({
            from_fiscal_year_id: Number(fromYearId),
            to_fiscal_year_id: Number(toYearId),
            approved_only: approvedOnly,
          });
          const data = res?.data?.data ?? res?.data ?? {};
          setResult(data);
          showSnackbar(`Rollover complete: ${data.copied ?? 0} copied, ${data.skipped_duplicates ?? 0} skipped`, 'success');
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        } finally {
          setSaving(false);
        }
      },
    });
  };

  return (
    <Box>
      <ModulePageHeader
        title="Budget Rollover"
        subtitle="Copy one year's budget lines into the next year as DRAFT"
      />
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <FiscalYearPicker label="Copy from (source year)" value={fromYearId} onChange={(id) => setFromYearId(id)} required />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FiscalYearPicker label="Copy into (target year)" value={toYearId} onChange={(id) => setToYearId(id)} required />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }} display="flex" alignItems="center">
              <FormControlLabel
                control={<Switch checked={approvedOnly} onChange={(e) => setApprovedOnly(e.target.checked)} />}
                label="Approved lines only"
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={runRollover} disabled={saving || !canCreate}>
              {saving ? 'Copying...' : 'Run Rollover'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {result && (
        <Card>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>Last rollover result</Typography>
            <Typography variant="body2">
              Copied <strong>{result.copied ?? 0}</strong> line{(result.copied ?? 0) === 1 ? '' : 's'} from year {result.from_fiscal_year_id} to year {result.to_fiscal_year_id}
              {(result.skipped_duplicates ?? 0) > 0 && (
                <>, skipped <strong>{result.skipped_duplicates}</strong> duplicate{(result.skipped_duplicates ?? 0) === 1 ? '' : 's'} already present in the target year</>
              )}.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Copied lines land as DRAFT in the target year - review them under Budgets before submitting.
            </Typography>
          </CardContent>
        </Card>
      )}

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </Box>
  );
};

export default RolloverTab;
